"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import type { VehicleType } from "~/lib/constants/vehicle";
import { buildEventSearchUrl } from "~/lib/events/event-search";
import { readLastVehicleSize, writeLastVehicleSize } from "~/lib/events/last-vehicle-size";
import type { DriverEventDay } from "~/server/api/routers/events";
import { api } from "~/trpc/react";

/**
 * Opens the existing search flow for a tapped event day, pre-filled, landing on
 * results rather than on a search form.
 *
 * Nothing about `/search` changes to make this work: this builds the same URL
 * `SearchCard` builds and pushes it. Everything the URL needs comes off the
 * occurrence except the vehicle size, which is resolved in this order:
 *
 *   1. the driver's default vehicle (`is_default`, falling back to the first —
 *      the same rule `VehicleSelector` uses at checkout);
 *   2. the size this browser last searched with;
 *   3. ask, once, and remember the answer.
 *
 * Almost nobody reaches step 3: `GET /events` is bearer-guarded, so every driver
 * who can see an event card is signed in, and a signed-in driver who has ever
 * booked has a vehicle. It exists for the driver who hasn't added one yet, and
 * it asks rather than assuming "standard" — `vehicle_type` filters which spots
 * come back, so a wrong guess shows a truck driver a list of spots that don't
 * fit and nothing on screen says why.
 *
 * A click that arrives before the vehicle list has settled is queued rather than
 * dropped, and the row reports itself busy via `busyKey` — for the whole wait,
 * including the navigation itself. Guessing early would mean prompting a driver
 * who has a perfectly good default vehicle still in flight.
 */
export function useOpenEventSearch() {
  const $ = _c(19);
  const router = useRouter();
  const [requested, setRequested] = useState(null);
  const [askingFor, setAskingFor] = useState(null);
  const [navigatingKey, setNavigatingKey] = useState(null);
  const t0 = requested !== null;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      enabled: t0,
      staleTime: 300000,
      retry: false
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const vehicles = api.vehicles.list.useQuery(undefined, t1);
  const navigatedRef = useRef(false);
  let t2;
  if ($[2] !== router) {
    t2 = (day, size) => {
      const url = buildEventSearchUrl(day, size);
      if (!url) {
        setRequested(null);
        setAskingFor(null);
        return;
      }
      navigatedRef.current = true;
      setAskingFor(null);
      setRequested(null);
      setNavigatingKey(day.key);
      router.push(url);
    };
    $[2] = router;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const navigate = t2;
  let t3;
  let t4;
  if ($[4] !== navigate || $[5] !== requested || $[6] !== vehicles.data || $[7] !== vehicles.isPending) {
    t3 = () => {
      if (!requested) {
        return;
      }
      if (vehicles.isPending) {
        return;
      }
      const list = vehicles.data ?? [];
      const preferred = list.find(_temp) ?? list[0];
      const size_0 = preferred?.size ?? readLastVehicleSize();
      if (size_0) {
        navigate(requested, size_0);
        return;
      }
      setAskingFor(requested);
      setRequested(null);
    };
    t4 = [requested, vehicles.isPending, vehicles.data, navigate];
    $[4] = navigate;
    $[5] = requested;
    $[6] = vehicles.data;
    $[7] = vehicles.isPending;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = day_0 => {
      if (navigatedRef.current) {
        return;
      }
      setRequested(day_0);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const openEventSearch = t5;
  let t6;
  if ($[11] !== askingFor || $[12] !== navigate) {
    t6 = size_1 => {
      if (!askingFor) {
        return;
      }
      writeLastVehicleSize(size_1);
      navigate(askingFor, size_1);
    };
    $[11] = askingFor;
    $[12] = navigate;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const confirmVehicleSize = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setAskingFor(null);
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const dismissVehiclePrompt = t7;
  const t8 = navigatingKey ?? requested?.key ?? null;
  let t9;
  if ($[15] !== askingFor || $[16] !== confirmVehicleSize || $[17] !== t8) {
    t9 = {
      openEventSearch,
      busyKey: t8,
      askingFor,
      confirmVehicleSize,
      dismissVehiclePrompt
    };
    $[15] = askingFor;
    $[16] = confirmVehicleSize;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
function _temp(vehicle) {
  return vehicle.is_default;
}